import { Button, DialogTrigger } from '@ibsys/kit';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AttributeResponse, ROOT_STRUCT_CODE, useAttributes } from '@/entities/attributes';
import { useDebouncedSearch } from '@/shared/hooks';
import { getOutsideTotal } from '@/shared/lib';
import { SelectionDialog, useLocalSelectionItems, useSelectionDialogState } from '@/shared/ui';
import type { GroupAttributeValue } from '../AttributeGroupForm/types';

/** Сколько атрибутов показывать в окне: список набирается поиском по всему набору, а не листается */
const ATTRIBUTES_PAGE_LIMIT = 50;

const getAttributeCode = (attribute: AttributeResponse) => attribute.code;
const getAttributeName = (attribute: AttributeResponse) => attribute.name;
const getAttributeDescription = (attribute: AttributeResponse) => attribute.description;

type Props = {
  /** Атрибуты, уже входящие в группу */
  existingAttributes: GroupAttributeValue[];
  onSave: (attributes: AttributeResponse[]) => void;
};

/** Кнопка «Добавить атрибут» и окно выбора атрибутов для группы */
export const AddAttributeDialog: FC<Props> = ({ existingAttributes, onSave }) => {
  const { t } = useTranslation(['common', 'attributeGroups']);

  const { isOpen, search, setSearch, selected, handleOpenChange, handleToggle } = useSelectionDialogState({
    getItemId: getAttributeCode,
  });
  const appliedSearch = useDebouncedSearch(search);

  // Поиск идёт по всему набору атрибутов: атрибут за пределами первой порции иначе недостижим.
  // Состав группы задаётся кодами корневых атрибутов — вложенные в структуры в окно не попадают.
  // Справочник нужен только выбору: закрытое окно его не запрашивает
  const { data, isLoading } = useAttributes(
    {
      search: appliedSearch || undefined,
      limit: ATTRIBUTES_PAGE_LIMIT,
      'is-deleted': false,
      'parent-struct-code': ROOT_STRUCT_CODE,
    },
    isOpen,
  );
  const loadedAttributes = data?.attributes ?? [];

  const items = useLocalSelectionItems({
    items: loadedAttributes,
    getItemId: getAttributeCode,
    // Выдача — корневые атрибуты: вложенный атрибут состава с тем же кодом корневого «тёзку» не скрывает
    excludedIds: existingAttributes
      .filter((attribute) => attribute.parentStructCode === ROOT_STRUCT_CODE)
      .map((attribute) => attribute.code),
  });

  const handleConfirm = () => {
    onSave(selected);
    handleOpenChange(false);
  };

  return (
    <SelectionDialog<AttributeResponse>
      open={isOpen}
      onOpenChange={handleOpenChange}
      trigger={
        <DialogTrigger asChild>
          <Button variant="secondary" size="sm" className="ml-auto">
            {t('attributeGroups:addAttribute')}
          </Button>
        </DialogTrigger>
      }
      title={t('attributeGroups:addAttribute')}
      search={search}
      onSearchChange={setSearch}
      items={items}
      // Атрибуты группы за выдачей окну неизвестны: из общего числа вычитаются только попавшие в неё
      total={getOutsideTotal(data?.total, loadedAttributes.length, loadedAttributes.length - items.length)}
      isLoading={isLoading}
      selected={selected}
      onToggle={handleToggle}
      getItemId={getAttributeCode}
      renderTitle={getAttributeName}
      renderHint={getAttributeCode}
      renderDescription={getAttributeDescription}
      labels={{
        empty: t('common:noItemsToSelect'),
        notFound: t('attributeGroups:noAttributesFound'),
        confirm: t('common:add'),
      }}
      onConfirm={handleConfirm}
    />
  );
};
